import { LicenseAnimationTextModel } from "./license.animation.text.model";
import { ImageLoader } from "./image-loader";

export class LicenseAnimationComponent {

    private imageLoader = new ImageLoader();
    private context: CanvasRenderingContext2D | null;
    private rafId: number | null = null;
    private lastTime = 0;

    // ================== STATE ==================
    private star = {
        positionX: 0,
        positionY: 0,
        peaks: 4,
        maxRadius: 260,
        minRadius: 1,
        angle: Math.PI
    };

    private oscillationRadii = { min: 260, max: 285 };
    private coordinateDeformationRatio = { x: 1, y: 2.7 };

    private accelerationAction = 0;
    private logoAccelerationAction = 0;
    private animateLeft = false;

    private timeFromLastStop = 0;

    // ================== CONSTANTS ==================
    private readonly MAX_DELTA = 0.05;

    private readonly ROTATION_SPEED = Math.PI * 0.5; // rad/sec
    private readonly ACCELERATION_SPEED = 1;
    private readonly LOGO_ACCELERATION_SPEED = 1.5;
    private readonly LOGO_MOVE_SPEED = 17;
    private readonly CHANGING_RADIUS_SIZE = {min: 3.5, max: 3.5} // коэффициент возврата в большой радиус
    private readonly ROTATION_PERIOD = 1.8;
    private readonly STOP_DURATION = 1;

    private logoMovementCoefficient = { left: -1.5, right: 1.5 };

    private textSettings = { size: 20, color: '#1D1C1C' };
    private checkMarkMargin = 20;
    private proMargin = 7;

    private readonly texts = [
        'Корпорация+',
        'Бизнес',
        'Корпорация',
        'Корпорация ФСТЭК'
    ];

    constructor(private canvas: HTMLCanvasElement) {
        this.context = canvas.getContext('2d');
    }

    // ================== INIT ==================
    async initialize(): Promise<void> {

        await Promise.all([
            this.imageLoader.load('logo', 'assets/images/animate/logo-animate.svg'),
            this.imageLoader.load('checkMark', 'assets/images/animate/check-mark.svg'),
            this.imageLoader.load('pro', 'assets/images/animate/pro.svg'),
        ]);

        this.resize(window.innerWidth);
        window.addEventListener('resize', () => this.resize(window.innerWidth));

        window.addEventListener('pageshow', (e) => {
            if (e.persisted) this.resetState();
        });

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) this.stop();
            else this.start();
        });

        this.resetState();
        this.start();
    }

    // ================== LOOP ==================
    private start() {
        if (this.rafId !== null) return;

        this.lastTime = performance.now();

        const tick = (time: number) => {
            const rawDelta = (time - this.lastTime) / 1000;
            const deltaTime = Math.min(rawDelta, this.MAX_DELTA);
            this.lastTime = time;

            this.update(deltaTime);
            this.clear();
            this.render();

            this.rafId = requestAnimationFrame(tick);
        };

        this.rafId = requestAnimationFrame(tick);
    }

    private stop() {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    // ================== UPDATE ==================
    private update(deltaTime: number) {

        this.timeFromLastStop += deltaTime;

        let rotating = false;

        if (this.timeFromLastStop > this.ROTATION_PERIOD) {
            rotating = true;

            const radiusRange = this.oscillationRadii.max - this.oscillationRadii.min;
            this.star.maxRadius = Math.max(
              this.oscillationRadii.min,
              this.star.maxRadius - radiusRange * this.CHANGING_RADIUS_SIZE.max * deltaTime
            );

            this.accelerationAction += this.ACCELERATION_SPEED * deltaTime;

            this.logoAccelerationAction +=
              (this.animateLeft ? -1 : 1) *
              this.LOGO_ACCELERATION_SPEED * deltaTime;

            if (this.timeFromLastStop >
              this.ROTATION_PERIOD + this.STOP_DURATION) {
                this.timeFromLastStop = 0;
            }

        } else {
            const radiusRange = this.oscillationRadii.max - this.oscillationRadii.min;
            this.star.maxRadius = Math.min(
              this.oscillationRadii.max,
              this.star.maxRadius + radiusRange * this.CHANGING_RADIUS_SIZE.min * deltaTime
            );

            this.accelerationAction += this.ACCELERATION_SPEED * deltaTime;
        }

        if (this.logoAccelerationAction > this.logoMovementCoefficient.right) {
            this.animateLeft = true;
        }
        if (this.logoAccelerationAction < this.logoMovementCoefficient.left) {
            this.animateLeft = false;
        }

        if (rotating) {
            this.star.angle += this.ROTATION_SPEED * deltaTime;
        }

        this.star.angle %= Math.PI * 2;
    }

    // ================== RENDER ==================
    private render() {
        if (!this.context) return;
        const ctx = this.context;

        const logo = this.imageLoader.get('logo');
        const logoOffsetX = this.logoAccelerationAction * this.LOGO_MOVE_SPEED;

        ctx.drawImage(
          logo.img,
          this.star.positionX - this.oscillationRadii.min / 2 + logoOffsetX,
          this.star.positionY - logo.height / 2,
          logo.width!,
          logo.height!
        );

        const delta = Math.PI / this.star.peaks;
        let textIndex = 0;
        const texts: LicenseAnimationTextModel[] = [];

        ctx.beginPath();

        for (let p = 0; p < this.star.peaks * 2; p++) {
            const r = p % 2 === 0 ? this.star.maxRadius : this.star.minRadius;

            const x = this.star.positionX +
              r / this.coordinateDeformationRatio.x *
              Math.sin(this.star.angle + p * delta);

            const y = this.star.positionY +
              r / this.coordinateDeformationRatio.y *
              Math.cos(this.star.angle + p * delta);

            ctx.lineTo(x, y);

            if (r === this.star.maxRadius) {
                const text = this.texts[textIndex];

                texts.push(new LicenseAnimationTextModel(
                  x, y,
                  this.textSettings.size,
                  this.textSettings.color,
                  text
                ));

                const check = this.imageLoader.get('checkMark');
                ctx.drawImage(
                  check.img,
                  x - ctx.measureText(text).width / 2 - check.width! - this.checkMarkMargin,
                  y - check.height! / 2,
                  check.width!,
                  check.height!
                );

                if (text === 'Корпорация+') {
                    const pro = this.imageLoader.get('pro');
                    ctx.drawImage(
                      pro.img,
                      x + ctx.measureText(text).width / 2 + this.proMargin,
                      y - pro.height! / 1.2,
                      pro.width!,
                      pro.height!
                    );
                }

                textIndex++;
            }
        }

        texts.forEach(t => t.draw(ctx));
        ctx.closePath();
    }

    // ================== UTILS ==================
    private clear() {
        this.context?.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    private async resize(width: number) {
        this.canvas.width = width <= 1380 ? 685 : width <= 1600 ? 785 : 885;
        this.canvas.height = width <= 1380 ? 220 : width <= 1600 ? 261 : 327;

        this.star.positionX = this.canvas.width / 2;
        this.star.positionY = this.canvas.height / 2;
        if (width > 0 && width <= 1380) {
            await this.imageLoader.load('logo', 'assets/images/animate/logo-animate1024.svg');
            await this.imageLoader.load('checkMark', 'assets/images/animate/check-mark1024.svg');
            await this.imageLoader.load('pro', 'assets/images/animate/pro1024.svg');
        } else if (width <= 1600) {
            await this.imageLoader.load('logo', 'assets/images/animate/logo-animate1440.png');
            await this.imageLoader.load('checkMark', 'assets/images/animate/check-mark1440.svg');
        } else {
            await this.imageLoader.load('logo', 'assets/images/animate/logo-animate.svg');
            await this.imageLoader.load('checkMark', 'assets/images/animate/check-mark.svg');
            await this.imageLoader.load('pro', 'assets/images/animate/pro.svg');
        }
        if (width <= 1380) {
            this.oscillationRadii = { min: 180, max: 200 };
            this.textSettings.size = 16;
            this.checkMarkMargin = 10;
        } else if (width <= 1600) {
            this.oscillationRadii = { min: 220, max: 245 };
            this.textSettings.size = 20;
            this.checkMarkMargin = 12;
            this.logoMovementCoefficient = { left: -1.25, right: 1.25 };
        } else {
            this.oscillationRadii = { min: 260, max: 285 };
            this.textSettings.size = 20;
            this.checkMarkMargin = 20;
        }

        this.resetState();
    }

    private resetState() {
        this.star.angle = Math.PI;
        this.star.maxRadius = this.oscillationRadii.max;
        this.accelerationAction = 0;
        this.logoAccelerationAction = 0;
        this.animateLeft = false;
        this.timeFromLastStop = 0;
    }
}
